import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { ColorPalette } from '../api/types';

// Default color palette (fallback when API doesn't provide colors)
export const DEFAULT_COLOR_PALETTE: ColorPalette = {
  primary: '#739156', // Primary - 500 (main olive green)
  secondary: '#4A5E37', // Primary - 700 (deep olive)
  background: '#FFFFFF',
  foregroundColor: '#3D4C2F', // Primary - 800 (darkest for text)
  border: '#D9E4CE', // Primary - 200 (light border)
  muted: '#5C7643', // Primary - 600 (muted/secondary text)
  destructive: '#FF5413',
  success: '#739156',
  warning: '#fc551d',
};

interface ColorContextType {
  colors: ColorPalette;
}

const ColorContext = createContext<ColorContextType | undefined>(undefined);

interface ColorProviderProps {
  children: ReactNode;
  colorPalettes?: ColorPalette;
}

export const ColorProvider: React.FC<ColorProviderProps> = ({ children, colorPalettes }) => {
  // Merge API colors with defaults
  const colors: ColorPalette = {
    ...DEFAULT_COLOR_PALETTE,
    ...colorPalettes,
  };

  // Apply CSS custom properties whenever colors change
  useEffect(() => {
    const root = document.documentElement;
    
    // Set CSS custom properties
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-background', colors.background);
    root.style.setProperty('--color-foreground', colors.foregroundColor);
    root.style.setProperty('--color-border', colors.border || DEFAULT_COLOR_PALETTE.border!);
    root.style.setProperty('--color-muted', colors.muted || DEFAULT_COLOR_PALETTE.muted!);
    root.style.setProperty('--color-destructive', colors.destructive || DEFAULT_COLOR_PALETTE.destructive!);
    root.style.setProperty('--color-success', colors.success || DEFAULT_COLOR_PALETTE.success!);
    root.style.setProperty('--color-warning', colors.warning || DEFAULT_COLOR_PALETTE.warning!);
    
    // Also set lighter/darker variants for primary color
    // These can be calculated or provided by the API
    root.style.setProperty('--color-primary-light', lightenColor(colors.primary, 20));
    root.style.setProperty('--color-primary-dark', darkenColor(colors.primary, 20));
  }, [colors]);

  const value: ColorContextType = {
    colors,
  };

  return <ColorContext.Provider value={value}>{children}</ColorContext.Provider>;
};

export const useColors = (): ColorContextType => {
  const context = useContext(ColorContext);
  if (context === undefined) {
    throw new Error('useColors must be used within a ColorProvider');
  }
  return context;
};

// Utility functions to lighten/darken colors
function lightenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, ((num >> 16) & 0xff) + amt);
  const G = Math.min(255, ((num >> 8) & 0xff) + amt);
  const B = Math.min(255, (num & 0xff) + amt);
  return `#${((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1)}`;
}

function darkenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, ((num >> 16) & 0xff) - amt);
  const G = Math.max(0, ((num >> 8) & 0xff) - amt);
  const B = Math.max(0, (num & 0xff) - amt);
  return `#${((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1)}`;
}
